/**
 * Alert visual-language tokens — Fork A.
 *
 * ONE source of truth for every alert-related surface: canvas empty state,
 * AlertCellNode, AlertFlowingEdge, AlertCellModal, and every tool-display
 * card (publish confirm/success, delete confirm/success, switch_mode alert
 * variant, find_automations, get_automation).
 *
 * Brand direction ("anime silver-blue metallic — even less blue, mostly tinge"):
 *  - Base surfaces are cool SLATE / SILVER (zinc). Blue is a WHISPER, not a wash.
 *  - Accent is icy pale blue (#bae6fd / #e0f2fe at low opacity).
 *  - Highlights are pearl-white with a micro-cool cast.
 *  - Destructive keeps the silver-slate family but darkens + adds a crisp icy
 *    outline — no red tint (user explicitly rejected red).
 *  - Research mode is neutral zinc (distinct from alert's icy tinge).
 *
 * Hard rules applied everywhere downstream:
 *  - Subtitles are small but NEAR-WHITE (not gray). Use `text-foreground/85`
 *    (token: `subLight`) minimum. Never `text-foreground/60` for subtitles.
 *  - No dead vertical space under an icon. Icons align to the title baseline,
 *    or cards use a flat single-row layout.
 *  - Corners: card rounded-lg, pill/chip rounded-md or rounded-full, button
 *    rounded-md. Never rounded-xl on tool-display cards.
 *  - Motion: 180-240ms ease [0.22, 1, 0.36, 1] for entrance; 600ms ease-out
 *    for success ring bursts. Respect prefers-reduced-motion via framer.
 *  - Spacing rhythm (Fibonacci-leaning): 4 / 8 / 13 / 21 (Tailwind approx:
 *    gap-1 / gap-2 / gap-[13px] or gap-3 / gap-5). Use these intentionally.
 */

export type AlertTone =
  | "icy"
  | "slate"
  | "destructive"
  | "research"
  | "amber"
  | "emerald";

// ─────────────────────────────────────────────────────────────────────────────
// Raw palette — keep these here, never hex elsewhere.
// ─────────────────────────────────────────────────────────────────────────────

export const PALETTE = {
  /** Accent — icy pale blue. Whisper, not wash. */
  icy: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
  },
  /** Cool slate / silver — the dominant base. */
  slate: {
    50: "#f8fafc",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
    950: "#020617",
  },
  /** Cool white — for pearl highlights + title text. */
  pearl: "#f5f7fa",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Token objects — each tone exports the full set. Consumers read only these.
// ─────────────────────────────────────────────────────────────────────────────

export interface ToneTokens {
  /** Card surface (bg class). Subtle — 3–6% tint over dark base. */
  surface: string;
  /** 1px border/ring class. */
  ring: string;
  /** Inner icon-square tint (bg). */
  iconBg: string;
  /** Icon foreground color class. */
  iconFg: string;
  /** Accent text color for emphasis (title links, "live" chip text). */
  accentText: string;
  /** Subtitle color — near-white, not gray. */
  subLight: string;
  /** Edge/line color (for flowing edges in the canvas). */
  edgeStroke: string;
  /** Glow rgba() value used in motion.boxShadow bursts. */
  glowRgba: string;
  /** Chip background for status pills ("LIVE · FIRST-RUN PASSED"). */
  chipBg: string;
  /** Chip text color. */
  chipText: string;
  /** Dot color inside chips. */
  chipDot: string;
}

/**
 * ICY — the alert-mode primary tone. Silver surface, whisper of icy blue —
 * BRIGHTENED one stop per user feedback ("colors too faint").
 * Sky-200 → sky-100 on accent text, edgeStroke 0.55 → 0.78, ring 0.25 → 0.35.
 */
export const ICY: ToneTokens = {
  surface: "bg-slate-900/55",
  ring: "border-slate-300/35",
  iconBg: "bg-sky-200/[0.09]",
  iconFg: "text-sky-100",
  accentText: "text-sky-100",
  subLight: "text-slate-50/90",
  edgeStroke: "rgba(186,230,253,0.78)", // icy-200 @ 78% — clearly visible
  glowRgba: "rgba(186,230,253,0.50)",
  chipBg: "bg-sky-200/15 border-sky-200/35",
  chipText: "text-sky-50",
  chipDot: "bg-sky-200",
};

/** SLATE — neutral surface, used for generic info / result states. */
export const SLATE: ToneTokens = {
  surface: "bg-slate-900/45",
  ring: "border-slate-400/15",
  iconBg: "bg-slate-100/[0.05]",
  iconFg: "text-slate-200",
  accentText: "text-slate-100",
  subLight: "text-slate-100/80",
  edgeStroke: "rgba(203,213,225,0.40)",
  glowRgba: "rgba(203,213,225,0.25)",
  chipBg: "bg-slate-200/10 border-slate-200/20",
  chipText: "text-slate-100",
  chipDot: "bg-slate-300",
};

/**
 * DESTRUCTIVE — delete flows. No red tint; signaled via darker slate surface
 * + crisper icy outline + icon/copy ("Delete").
 */
export const DESTRUCTIVE: ToneTokens = {
  surface: "bg-slate-950/75",
  ring: "border-slate-300/40",
  iconBg: "bg-slate-100/[0.08]",
  iconFg: "text-slate-100",
  accentText: "text-slate-50",
  subLight: "text-slate-100/85",
  edgeStroke: "rgba(226,232,240,0.55)",
  glowRgba: "rgba(226,232,240,0.30)",
  chipBg: "bg-slate-100/10 border-slate-100/25",
  chipText: "text-slate-50",
  chipDot: "bg-slate-200",
};

/** RESEARCH — switch_mode("research") variant; distinct from icy. */
export const RESEARCH: ToneTokens = {
  surface: "bg-zinc-900/50",
  ring: "border-zinc-400/20",
  iconBg: "bg-zinc-100/[0.05]",
  iconFg: "text-zinc-200",
  accentText: "text-zinc-100",
  subLight: "text-zinc-100/80",
  edgeStroke: "rgba(212,212,216,0.40)",
  glowRgba: "rgba(212,212,216,0.22)",
  chipBg: "bg-zinc-200/10 border-zinc-200/20",
  chipText: "text-zinc-100",
  chipDot: "bg-zinc-300",
};

/** AMBER — pause / caution confirmations. Warm but still on-brand dark slate base. */
export const AMBER: ToneTokens = {
  surface: "bg-slate-900/55",
  ring: "border-amber-300/35",
  iconBg: "bg-amber-400/[0.14]",
  iconFg: "text-amber-200",
  accentText: "text-amber-100",
  subLight: "text-slate-50/90",
  edgeStroke: "rgba(251,191,36,0.55)",
  glowRgba: "rgba(251,191,36,0.35)",
  chipBg: "bg-amber-400/15 border-amber-300/35",
  chipText: "text-amber-50",
  chipDot: "bg-amber-300",
};

/** EMERALD — resume / go-live confirmations. */
export const EMERALD: ToneTokens = {
  surface: "bg-slate-900/55",
  ring: "border-emerald-300/35",
  iconBg: "bg-emerald-400/[0.14]",
  iconFg: "text-emerald-200",
  accentText: "text-emerald-100",
  subLight: "text-slate-50/90",
  edgeStroke: "rgba(52,211,153,0.55)",
  glowRgba: "rgba(52,211,153,0.35)",
  chipBg: "bg-emerald-400/15 border-emerald-300/35",
  chipText: "text-emerald-50",
  chipDot: "bg-emerald-300",
};

export const TONES: Record<AlertTone, ToneTokens> = {
  icy: ICY,
  slate: SLATE,
  destructive: DESTRUCTIVE,
  research: RESEARCH,
  amber: AMBER,
  emerald: EMERALD,
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared text / layout tokens.
// ─────────────────────────────────────────────────────────────────────────────

/** Title class — same across all alert surfaces. */
export const TITLE_CLASS = "text-sm font-semibold text-slate-50 leading-tight";
/** Subtitle class — small but NEAR-WHITE, never gray (brighter than before). */
export const SUB_CLASS = "text-xs text-slate-50/90 leading-snug";
/** Body copy. */
export const BODY_CLASS = "text-sm text-slate-100/90 leading-relaxed";
/** Monospace (code previews, tracebacks). */
export const MONO_CLASS = "font-mono text-[10.5px] text-slate-200/90";

/** Card shell padding presets (Fibonacci-ish). Tightened for compact views. */
export const PAD = {
  dense: "px-2.5 py-1.5",
  default: "px-3 py-2.5",
  confirm: "px-4 py-3",
} as const;

/** Radius presets — sharper than the prior amber design. */
export const RADIUS = {
  card: "rounded-lg",
  chip: "rounded-md",
  pill: "rounded-full",
  button: "rounded-md",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Motion presets.
// ─────────────────────────────────────────────────────────────────────────────

export const SPRING_IN = {
  initial: { opacity: 0, y: 4, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const },
};

export const SUCCESS_BURST = {
  initial: { boxShadow: "0 0 0 0 rgba(186,230,253,0.45)" },
  animate: { boxShadow: "0 0 0 12px rgba(186,230,253,0)" },
  transition: { duration: 0.6, ease: "easeOut" as const },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers.
// ─────────────────────────────────────────────────────────────────────────────

/** Pick a ToneTokens object by tone key. Safe default = slate. */
export function tone(t: AlertTone | undefined): ToneTokens {
  return TONES[t ?? "slate"];
}

/**
 * Pick the primary alert/canvas tone from the page view mode.
 * Signals are emerald/green; everything else stays icy blue.
 * Single source of truth so every canvas surface switches consistently.
 */
export function alertTone(viewMode: string | undefined): ToneTokens {
  return viewMode === "signal" ? EMERALD : ICY;
}
